








import Enumerable from "linq";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { Department } from "../../../../../../domain/department";
import { IClaioImageSource } from "../../../../../../domain/claio";
import { StandardOption } from "../../../../../system/selectors/mixins";
import OptionDropDownSelector from "../../../../../system/selectors/OptionDropDownSelector.vue";
import { ClaioContentsFilterMixin } from "./mixins";

@Component({
  components: {
    OptionDropDownSelector,
  }
})
export default class ClaioContentsDropDownFilter extends Mixins(ClaioContentsFilterMixin) {
  @Prop()
  shouldFilterImagesByOrderNo: boolean;

  @Prop()
  department: Department;

  editingSource: IClaioImageSource[];

  get dropDownOptions(): StandardOption<IClaioImageSource>[] {
    return Enumerable.from([ new StandardOption<IClaioImageSource>("未指定", null) ])
      .concat(this.imageSourceOptions)
      .toArray();
  }

  get imageSourceOptions(): StandardOption<IClaioImageSource>[] {
    // サムネイルを昇順で表示するときでもプルダウンは降順で表示したいので、ここで辻褄をあわせている
    // concat()しないと元のインスタンスの順序が変更されてしまうので注意
    const descendingImageSource = this.shouldOrderByAscending
      ? this.source.concat().reverse()
      : this.source;
    return Enumerable.from(descendingImageSource)
      .distinct(s => s.imageDate.toDateString() + s.departmentId)
      .select(s => new StandardOption<IClaioImageSource>(this.getImageSourceOptionName(s), s))
      .toArray();
  }

  get initialImageSource(): IClaioImageSource {
    if (this.shouldFilterImagesByOrderNo) {
      // オーダ番号絞り込みしている場合はフィルタ選択しない
      return null;
    }
    const imageSource = this.$configuration.behavior.document.editor.contents.claio.filters.shouldPreferTodayImage
      ? this.getTodayPreferredInitialImageSource()
      : this.getDepartmentPreferredInitialImageSource();
    return this.getSelectableImageSourceOptionValue(imageSource);
  }

  private getSelectableImageSourceOptionValue(imageSource: IClaioImageSource): IClaioImageSource {
    if (!imageSource) { return null; }
    const option = Enumerable.from(this.imageSourceOptions).firstOrDefault(s => s.label === this.getImageSourceOptionName(imageSource));
    return option
      ? option.value
      : null;
  }

  private getTodayPreferredInitialImageSource(): IClaioImageSource {
    const today = new Date();
    const todayImages = Enumerable.from(this.source)
      .where(s => this.isSameDate(s.imageDate, today))
      .toArray();
    
    if (todayImages.length === 0) {
      // 本日の画像が無い場合は未選択
      return null;
    }
    const departmentIds = Enumerable.from(todayImages)
      .select(i => i.departmentId)
      .distinct()
      .toArray();

    if (departmentIds.length === 1) {
      // 診療科がひとつの場合はそれを選択
      return todayImages[0];
    }
    if (!this.department) {
      // 診療科が複数あるが上位から診療科の指定が無い場合は未選択
      return null;
    }
    const todayDepartmentImages = Enumerable.from(todayImages)
      .where(i => i.departmentId === this.department.id)
      .toArray();

    // 本日の画像のうち、診療科一致するものがあればそれを選択、無ければ未選択
    return (todayDepartmentImages.length !== 0)
      ? todayDepartmentImages[0]
      : null;
  }

  private isSameDate(source: Date, target: Date): boolean {
    if (!source || !target) { return false; }
    return source.getFullYear() === target.getFullYear()
      && source.getMonth() === target.getMonth()
      && source.getDate() === target.getDate();
  }

  private getDepartmentPreferredInitialImageSource(): IClaioImageSource {
    if (!this.department) { return null; }
    const hasDepartmentImage = Enumerable.from(this.source).any(s => s.departmentId === this.department.id);
    return Enumerable.from(this.source)
      .orderByDescending(s => s.imageDate)
      .firstOrDefault(s => !hasDepartmentImage || (s.departmentId === this.department.id));
  }

  @Watch("selectedSource")
  onSelectedSourceUpdated(newValue: IClaioImageSource) {
    this.editingSource = newValue !== null
      ? Enumerable.from(this.source)
        .where(s => s.imageDate.toDateString() === newValue.imageDate.toDateString() && s.departmentId === newValue.departmentId)
        .toArray()
      : (this.source ? this.source.concat() : []); // concatしないと「手書きレイヤーの描画」のチェック変更で意図しない範囲に更新が伝播してしまう
  }

  @Watch("source")
  onSourceUpdated(newValue: IClaioImageSource[]) {
    this.editingSource = newValue;
    this.selectedSource = this.initialImageSource;
  }
}
